import type React from 'react';
import { useEffect, useState } from 'react';

export interface JitsiError {
  id: string;
  name: string;
  type: string;
  message?: string;
  isFatal: boolean;
  timestamp: number;
}

interface JitsiErrorBannerProps {
  errors: JitsiError[];
  onDismiss: (id: string) => void;
}

const AUTO_DISMISS_MS = 8000;

const InfoTooltip: React.FC<{ type: string; name: string }> = ({ type, name }) => {
  const [visible, setVisible] = useState(false);

  return (
    <button
      type="button"
      className="jitsi-error-banner__info"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-label="Show error details"
    >
      &#9432;
      {visible && (
        <span className="jitsi-error-banner__tooltip" role="tooltip">
          Type={type}, Name={name}
        </span>
      )}
    </button>
  );
};

const ErrorRow: React.FC<{ error: JitsiError; onDismiss: (id: string) => void }> = ({
  error,
  onDismiss,
}) => {
  useEffect(() => {
    if (error.isFatal) {
      return;
    }
    const timer = setTimeout(() => onDismiss(error.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [error.id, error.isFatal, onDismiss]);

  return (
    <div
      className={`jitsi-error-banner ${error.isFatal ? 'jitsi-error-banner--fatal' : 'jitsi-error-banner--warning'}`}
      role="alert"
    >
      <span className="jitsi-error-banner__message">
        {error.message || error.name}
        <InfoTooltip type={error.type} name={error.name} />
      </span>
      <button
        type="button"
        className="jitsi-error-banner__close"
        onClick={() => onDismiss(error.id)}
        aria-label="Dismiss error"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>
  );
};

export const JitsiErrorBanner: React.FC<JitsiErrorBannerProps> = ({ errors, onDismiss }) => {
  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="jitsi-error-banner-container">
      {errors.map((error) => (
        <ErrorRow key={error.id} error={error} onDismiss={onDismiss} />
      ))}
    </div>
  );
};
